import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';

import { useRecoilValue } from 'recoil';

import { cn, hasModule, iaRa, inRn } from 'lib/helpers/utility';
import { useInvoiceEditContext } from 'lib/context/InvoiceContext/InvoiceContext';
import { useOneInternalInvoice } from 'lib/hooks/queries/billing/useOneInternalInvoice';

import { modulesState } from 'components/state';
import { showAlert } from 'components/shared/Alert/Alert';
import AGBasicTable from 'components/shared/AGBasicTable/AGBasicTable';
import CustomNoRowsOverlay from 'components/shared/AGTable/customNoRowsOverlay';
import CustomLoadingOverlay from 'components/shared/AGTable/customLoadingOverlay';

import { EDIT_INVOICE_DEFAULT_COLUMN_DEFS, getEditInvoiceColumnDefs } from './lib/columnDefs';
import { addTempIdToInvoice, addTempIdToItem, removeTempIdFromInvoice } from './lib/tempIdHandler';
import {
  calculateInvoiceDiscountFromItems,
  calculateInvoiceTaxFromItems,
  handleProcedureType
} from './lib/helpers';

const EditInvoiceModal = forwardRef(({ invoiceId }, ref) => {
  const { editedInvoice, setEditedInvoice } = useInvoiceEditContext();
  const gridRef = useRef(null);
  const containerRef = useRef(null);
  const previousInvoiceIdRef = useRef(null);
  const editingOriginalValuesRef = useRef({});
  const initialInvoiceDiscountRef = useRef(0);

  const { data, isLoading } = useOneInternalInvoice({
    params: { id: invoiceId },
    dependencies: [invoiceId],
    options: {
      enabled: Boolean(invoiceId),
      staleTime: 0,
      refetchOnMount: true
    }
  });

  const practiceModules = useRecoilValue(modulesState);
  const hasTaxModule = hasModule(practiceModules, 'medipay.tax');

  const hasActiveInsurance = iaRa(data?.invoice?.patient?.insuranceProfile).some(
    (p) => p?.type === 'primary' && p?.status === 'active'
  );

  const columnDefs = useMemo(
    () => getEditInvoiceColumnDefs({ hasActiveInsurance, hasTaxModule }),
    [hasActiveInsurance, hasTaxModule]
  );

  useEffect(() => {
    const invoiceIdChanged = previousInvoiceIdRef.current !== invoiceId;
    const shouldInitialize = invoiceIdChanged || !editedInvoice;

    if (data?.invoice && shouldInitialize) {
      const newInvoice = addTempIdToInvoice(data?.invoice);
      setEditedInvoice(newInvoice);
      previousInvoiceIdRef.current = invoiceId;
      initialInvoiceDiscountRef.current = calculateInvoiceDiscountFromItems(data.invoice);
    }
  }, [invoiceId, setEditedInvoice, data]);

  const collectInvoiceFromGrid = useCallback(() => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) return editedInvoice;

    const allRows = [];
    gridApi.forEachNode((node) => {
      if (node.data) {
        allRows.push(node.data);
      }
    });

    const procedures = allRows.filter(
      (row) =>
        !row.type || row.type === 'primary' || row.type === 'self_pay' || row.type === 'unapplied'
    );
    const products = allRows.filter((row) => row.type === 'Product');
    const packages = allRows.filter((row) => row.type === 'Package');

    const updatedInvoice = {
      ...editedInvoice,
      procedures,
      products,
      packages
    };

    const taxCents = calculateInvoiceTaxFromItems(updatedInvoice);
    const discountCents = calculateInvoiceDiscountFromItems(updatedInvoice);

    return {
      ...updatedInvoice,
      tax: taxCents,
      discount: discountCents
    };
  }, [editedInvoice]);

  useImperativeHandle(
    ref,
    () => ({
      prepareInvoiceForSave: () => {
        const finalInvoice = collectInvoiceFromGrid();

        const invoiceBalanceCents = inRn(finalInvoice?.invoice_balance);
        const totalDiscountCents = inRn(finalInvoice?.discount);
        const initialDiscountCents = inRn(initialInvoiceDiscountRef.current);

        const maxAllowedDiscountCents = invoiceBalanceCents + initialDiscountCents;

        if (maxAllowedDiscountCents >= 0 && totalDiscountCents > maxAllowedDiscountCents) {
          showAlert({
            title: 'Discount exceeds invoice balance',
            message:
              'The total discount across all items cannot be greater than the allowed invoice balance. Please adjust the discounts before saving.',
            color: 'warning'
          });
          return null;
        }

        const updatedInvoice = removeTempIdFromInvoice(finalInvoice);
        setEditedInvoice(updatedInvoice);
        return updatedInvoice;
      }
    }),
    [collectInvoiceFromGrid, setEditedInvoice]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const gridApi = gridRef.current?.api;
      if (!gridApi || !containerRef.current) return;

      if (containerRef.current.contains(event.target)) return;

      const editingCells = gridApi.getEditingCells();
      if (editingCells.length > 0) {
        gridApi.stopEditing();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const rowData = useMemo(() => {
    const invoice = editedInvoice || data?.invoice;
    if (!invoice) return [];

    const invoiceBalance = inRn(invoice?.invoice_balance);
    const initialDiscountCents = inRn(initialInvoiceDiscountRef.current);

    const procedures = iaRa(invoice.procedures).map((procedure) => ({
      ...addTempIdToItem(procedure),
      type: procedure?.type || 'Package',
      invoice_balance: invoiceBalance,
      initial_discount: initialDiscountCents,
      row_initial_discount_cents: inRn(procedure?.discount?.amount_cents)
    }));
    const products = iaRa(invoice.products).map((product) => ({
      ...addTempIdToItem(product),
      type: 'Product',
      invoice_balance: invoiceBalance,
      initial_discount: initialDiscountCents,
      row_initial_discount_cents: inRn(product?.discount?.amount_cents)
    }));
    const packages = iaRa(invoice.packages).map((pkg) => ({
      ...addTempIdToItem(pkg),
      type: 'Package',
      invoice_balance: invoiceBalance,
      initial_discount: initialDiscountCents,
      row_initial_discount_cents: inRn(pkg?.discount?.amount_cents)
    }));
    return [...procedures, ...products, ...packages];
  }, [editedInvoice, data?.invoice]);

  const calcHeight = useMemo(() => {
    return iaRa(rowData).length * 40 + 52 + 45; // 40px is the height of the row, 52px is the height of the footer, 45px is the height of the header
  }, [rowData]);

  const onCellEditingStarted = (params) => {
    const { column, data: rowData } = params;
    const field = column.colDef.field;
    const rowId = rowData.tempId;

    if (!editingOriginalValuesRef.current[rowId]) {
      editingOriginalValuesRef.current[rowId] = {};
    }
    editingOriginalValuesRef.current[rowId][field] = rowData[field];
  };

  const onCellEditingStopped = async (params) => {
    const { column, data: rowData, newValue, node } = params;
    const field = column.colDef.field;
    const rowId = rowData.tempId;

    const originalValue = editingOriginalValuesRef.current[rowId]?.[field];

    if (editingOriginalValuesRef.current[rowId]) {
      delete editingOriginalValuesRef.current[rowId][field];
    }

    if (newValue === originalValue) return;

    if (field === 'type') {
      try {
        const updatedProcedure = await handleProcedureType(rowData, newValue, rowData.id);

        node.setData(updatedProcedure);

        const api = gridRef.current?.api;
        if (api) {
          api.refreshCells({
            rowNodes: [node],
            force: true
          });
        }
      } catch (error) {
        console.error('Error updating procedure type:', error);
        node.setData({ ...rowData, type: originalValue });
      }
    }

    if (field === 'sales_count' && rowData.type === 'Product') {
      const updatedProduct = {
        ...rowData,
        sales_count: newValue,
        total_amount_cents: (rowData.amount_cents || 0) * (newValue || 1)
      };
      node.setData(updatedProduct);
    }
  };

  const components = useMemo(
    () => ({
      agLoadingOverlay: CustomLoadingOverlay,
      agNoRowsOverlay: CustomNoRowsOverlay
    }),
    []
  );

  return (
    <div
      ref={containerRef}
      className={cn('max-h-[calc(50vh-80px)] min-h-[160px] w-full sm:max-h-[calc(50vh-32px)]')}
      style={{ height: `${calcHeight}px` }}>
      <AGBasicTable
        key={invoiceId}
        columnDefs={columnDefs}
        defaultColDef={EDIT_INVOICE_DEFAULT_COLUMN_DEFS}
        rowData={rowData}
        gridRef={gridRef}
        loading={isLoading}
        components={components}
        singleClickEdit={true}
        getRowId={(params) => params.data.tempId}
        onCellEditingStarted={onCellEditingStarted}
        onCellEditingStopped={onCellEditingStopped}
        stopEditingWhenCellsLoseFocus={true}
        immutableData={true}
      />
    </div>
  );
});

EditInvoiceModal.displayName = 'EditInvoiceModal';

export default EditInvoiceModal;
